import { h, Fragment } from 'preact';
import { useMemo } from 'preact/hooks';
import clsx from 'clsx';
import { withCartContext } from './cart-context';
import CartLineAttributes from './cart-line-attributes';
import Image from '../utils/image';
import Price from '../product/product-price';
import PlusCart from '../utils/icon/plus-cart.svg';
import MinusCart from '../utils/icon/minus-cart.svg';
import Storefront from '../utils/storefront';

const CartItem = (props) => {
  const { cart, line, isGwp } = props;
  const isSendGiftcard = line.properties?._gift_id;
  const isInCents = cart.mode !== 'storefront';
  const productHandle = line.merchandise?.product.handle || line.handle;
  const productTitle = line.merchandise?.product.title || line.product_title;
  const totalAmount = line.cost?.totalAmount || {
    amount: line.original_line_price,
    currencyCode: window.TMW?.localization?.currencyCode || 'USD',
  };
  const subtotalAmount = {
    amount:
      line.properties && line.properties._gwp_price
        ? line.properties._gwp_price * 100 * line.quantity
        : false,
    currencyCode: window.TMW?.localization?.currencyCode || 'USD',
  };

  const comparePrice =
    line.merchandise?.compareAtPrice?.amount ||
    parseInt(line.properties?._compare_price, 10);

  const totalCompare = comparePrice
    ? {
        amount: parseFloat(comparePrice) * line.quantity,
        currencyCode: window.TMW?.localization?.currencyCode || 'USD',
      }
    : false;

  const storefront = new Storefront();
  const quantityLimit = storefront.data?.quantityLimit;
  const {
    enabled: quantityLimitEnabled,
    maxCartQuantity,
    maxProductQuantity,
  } = quantityLimit;

  const cartTotalQuantity = cart.state.items
    .map((item) => item.quantity)
    .reduce((acc, current) => acc + current, 0);

  const handleAdjustQuantity = (quantity) => {
    let payload = [
      {
        id: line.id,
        quantity,
      },
    ];

    // If bundle, add children to update request
    if (line.children) {
      payload = line.children.map((child) => ({
        id: child.id,
        attributes: child.attributes,
        quantity,
      }));
    }

    return cart.update(payload);
  };

  const handleRemove = (isGWP = false) => {
    let payload = [line.id];

    // If bundle, add children to update request
    if (line.children) {
      payload = line.children.map((child) => child.id);
    }

    if (isGWP && line?.properties?._gwp_handle) {
      const connectorHandle = line?.properties?._gwp_handle;
      const date = new Date();
      date.setTime(date.getTime() + 24 * 60 * 60 * 1000);
      // eslint-disable-next-line max-len
      document.cookie = `disabled_gwp-${connectorHandle}=1; expires=${date.toUTCString()}; path=/`;
    }
    return cart.remove(payload);
  };

  const lineItem = useMemo(
    () => (
      <div
        key={line.id}
        className="flex justify-between gap-2.5 laptop:gap-3 border-b border-black-30 last:border-b-0 py-5"
      >
        <div className="w-[92px] min-w-[92px] laptop:w-[138px] laptop:min-w-[138px] ">
          <div className="bg-cream-grey">
            <Image
              {...(line.merchandise
                ? line.merchandise.image
                : line.featured_image)}
              srcWidths={{ sm: 640, md: 768, lg: 1024, xl: 1280, '2xl': 1536 }}
              classes="mix-blend-multiply"
            />
          </div>
        </div>
        <div className="flex flex-col gap-3 laptop:gap-[28px] w-full justify-between">
          <div className="flex justify-between items-start gap-[18px] laptop:gap-8">
            <div className="flex flex-col gap-1 laptop:gap-1.5">
              <a
                href={
                  !isGwp
                    ? `${window.Shopify.routes.root}products/${productHandle}`
                    : '#'
                }
                className={clsx(
                  'font-13 text-black',
                  isGwp && 'pointer-events-none',
                )}
              >
                {productTitle}
              </a>
              <CartLineAttributes line={line} />
            </div>
            <Price
              currentPrice={!isGwp ? totalAmount : subtotalAmount}
              currentComparePrice={totalCompare}
              fontClass="font-13 text-black"
              customContainerClass="flex-col !gap-0 items-end"
              isGWP={isGwp}
              cents={isInCents}
            />
          </div>
          <div className="flex justify-between w-full items-end">
            {!isGwp && !isSendGiftcard ? (
              <div className="rounded-[3px] overflow-hidden border-[0.75px] border-black-30 bg-transparent p-1 flex gap-1.5">
                <button
                  onClick={() => handleAdjustQuantity(line.quantity - 1)}
                  aria-label={TMW.Strings.cart.remove}
                  className="py-2.5 px-[5px]"
                >
                  <MinusCart />
                </button>
                <input
                  type="number"
                  value={line.quantity}
                  onBlur={(e) =>
                    handleAdjustQuantity(parseInt(e.target.value, 10))
                  }
                  max={maxProductQuantity}
                  className="bg-transparent border-0 font-13 text-black pointer-events-none p-0 w-[27px] text-center"
                />
                <button
                  onClick={() => handleAdjustQuantity(line.quantity + 1)}
                  disabled={
                    (quantityLimitEnabled &&
                      line.quantity === maxProductQuantity) ||
                    cartTotalQuantity === maxCartQuantity
                  }
                  className="disabled:opacity-20 py-2.5 px-[5px]"
                  aria-label={TMW.Strings.cart.add}
                >
                  <PlusCart />
                </button>
              </div>
            ) : (
              <>
                {isGwp ? (
                  <p className="font-13 text-black">
                    <span className="mr-2">QTY:</span>
                    {line.quantity}
                  </p>
                ) : (
                  <div></div>
                )}
              </>
            )}
            <button
              onClick={() => handleRemove(isGwp)}
              className="underline text-charcoal utility-2"
            >
              {TMW.Strings.cart.remove}
            </button>
          </div>
        </div>
      </div>
    ),
    [line, totalCompare],
  );

  return lineItem;
};

export default withCartContext(CartItem);
